"use client";

import React from "react";
import "./dashboardSkelton.css";

/**
 * Tiny primitives for consistent skeletons
 */
const SkLine = ({ w = "100%", h = 12, className = "" }) => (
  <span
    className={`sk sk-line ${className}`}
    style={{ width: w, height: h }}
    aria-hidden="true"
  />
);
const SkCircle = ({ size = 32, className = "" }) => (
  <span
    className={`sk sk-circle ${className}`}
    style={{ width: size, height: size }}
    aria-hidden="true"
  />
);
const SkBar = ({ pct = 60 }) => (
  <span className="sk sk-bar" aria-hidden="true">
    <span className="sk sk-bar-fill" style={{ width: `${pct}%` }} />
  </span>
);

const StatSkeleton = () => (
  <div className="stat-item-new sk-card">
    <div className="stat-icon-new">
      <SkCircle size={24} />
    </div>
    <div className="stat-content-new">
      <SkLine w="48px" h={18} />
      <SkLine w="80px" h={12} className="sk-mt-6" />
    </div>
  </div>
);

const QuickActionSkeleton = () => (
  <button className="header-action-btn sk-button" disabled aria-disabled="true">
    <SkCircle size={20} />
    <SkLine w="110px" h={14} className="sk-ml-8" />
  </button>
);

const MetricCardSkeleton = () => (
  <div className="metric-card sk-card">
    <div className="metric-card__header">
      <SkCircle size={24} />
      <SkLine w="55%" h={14} className="sk-ml-8" />
    </div>
    <div className="metric-card__body">
      <SkLine w="40%" h={28} />
      <SkLine w="65%" h={12} className="sk-mt-10" />
      <SkLine w="45%" h={12} className="sk-mt-6" />
    </div>
    <div className="metric-card__footer">
      <SkBar pct={72} />
    </div>
  </div>
);

const ListItemWithBarSkeleton = ({
  titleW = "40%",
  subW = "30%",
  barPct = 55,
}) => (
  <div className="list-item-with-bar">
    <div className="list-item-info">
      <SkLine w={titleW} h={14} />
      <SkLine w={subW} h={12} className="sk-mt-6" />
    </div>
    <div className="list-item-metrics">
      <div className="performance-score">
        <SkLine w="44px" h={18} />
      </div>
      <div className="performance-bar">
        <SkBar pct={barPct} />
      </div>
    </div>
  </div>
);

const SectionHeaderSkeleton = ({ icon = false, w = "30%" }) => (
  <div className="widget-header">
    <div className="widget-title">
      {icon ? <SkCircle size={20} /> : null}
      <SkLine w={w} h={18} className={icon ? "sk-ml-8" : ""} />
    </div>
  </div>
);

const DashboardSkeleton = () => {
  return (
    <div
      className="dashboard is-skeleton"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <main className="dashboard-main">
        <div className="container">
          {/* Welcome Section */}
          <div className="welcome-section-new">
            <div className="welcome-header-container">
              <div className="welcome-content-new">
                <SkLine w="38%" h={28} />
                <SkLine w="60%" h={14} className="sk-mt-8" />

                <div className="welcome-stats-new sk-mt-16">
                  <StatSkeleton />
                  <StatSkeleton />
                  <StatSkeleton />
                </div>
              </div>

              <div style={{ paddingLeft: "20px" }}>
                <SkLine w="120px" h={18} />
                <div className="header-quick-actions sk-mt-12">
                  <QuickActionSkeleton />
                  <QuickActionSkeleton />
                  <QuickActionSkeleton />
                  <QuickActionSkeleton />
                </div>
              </div>
            </div>
          </div>

          {/* Key Performance Metrics */}
          <div className="metrics-overview">
            <h2 className="left-aligned">
              <SkCircle size={24} />
              <SkLine w="220px" h={18} className="sk-ml-8" />
            </h2>
            <div className="metrics-grid">
              <MetricCardSkeleton />
              <MetricCardSkeleton />
              <MetricCardSkeleton />
              <MetricCardSkeleton />
            </div>
          </div>

          {/* Main Grid */}
          <div className="dashboard-grid">
            {/* Left Column - Critical Skill Gaps */}
            <div className="dashboard-left">
              <div className="dashboard-widget elegant-card equal-height-card">
                <SectionHeaderSkeleton icon />
                <div className="card-description">
                  <SkLine w="80%" h={12} />
                </div>
                <div className="skill-gaps-list sk-mt-12">
                  <div className="skill-gap-item">
                    <div className="skill-gap-info">
                      <SkLine w="40%" h={14} />
                      <SkLine w="30%" h={12} className="sk-mt-6" />
                    </div>
                    <div className="skill-gap-meter">
                      <SkBar pct={65} />
                    </div>
                    <SkCircle size={20} />
                  </div>

                  <div className="skill-gap-item">
                    <div className="skill-gap-info">
                      <SkLine w="44%" h={14} />
                      <SkLine w="28%" h={12} className="sk-mt-6" />
                    </div>
                    <div className="skill-gap-meter">
                      <SkBar pct={52} />
                    </div>
                    <SkCircle size={20} />
                  </div>

                  <div className="skill-gap-item">
                    <div className="skill-gap-info">
                      <SkLine w="36%" h={14} />
                      <SkLine w="32%" h={12} className="sk-mt-6" />
                    </div>
                    <div className="skill-gap-meter">
                      <SkBar pct={74} />
                    </div>
                    <SkCircle size={20} />
                  </div>

                  <div className="skill-gap-item">
                    <div className="skill-gap-info">
                      <SkLine w="42%" h={14} />
                      <SkLine w="24%" h={12} className="sk-mt-6" />
                    </div>
                    <div className="skill-gap-meter">
                      <SkBar pct={40} />
                    </div>
                    <SkCircle size={20} />
                  </div>
                </div>
                <div className="widget-footer">
                  <SkLine w="120px" h={14} />
                </div>
              </div>
            </div>

            {/* Center Column - Team Performance */}
            <div className="dashboard-center">
              <div className="dashboard-widget elegant-card equal-height-card">
                <SectionHeaderSkeleton icon />
                <div className="card-description">
                  <SkLine w="85%" h={12} />
                </div>

                <div className="team-performance-list sk-mt-12">
                  <ListItemWithBarSkeleton
                    titleW="30%"
                    subW="22%"
                    barPct={82}
                  />
                  <ListItemWithBarSkeleton
                    titleW="34%"
                    subW="24%"
                    barPct={68}
                  />
                  <ListItemWithBarSkeleton
                    titleW="28%"
                    subW="20%"
                    barPct={56}
                  />
                  <ListItemWithBarSkeleton
                    titleW="32%"
                    subW="18%"
                    barPct={44}
                  />
                </div>

                <div className="widget-footer">
                  <SkLine w="100px" h={14} />
                </div>
              </div>
            </div>

            {/* (Right column is commented out in your dashboard; no skeleton needed) */}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardSkeleton;
